/**
 * Privilege Service
 * 
 * This service manages role-based permissions and privileges
 * It provides a central place to check, get, and set permissions
 * It uses localStorage for persistent storage
 */

import { getUserRole } from './authService';

// Default roles and their inherent access levels
const ROLES = {
  ADMIN: 'ADMIN',
  MANAGER: 'MANAGER',
  EXECUTIVE: 'EXECUTIVE',
  BILLING_PERSON: 'BILLING_PERSON',
  USER: 'USER'
};

// Default menu structure
const MENUS = {
  DASHBOARD: '/dashboard',
  INVENTORY: '/inventory',
  INVENTORY_CATEGORIES: '/inventory/categories',
  INVENTORY_PRODUCTS: '/inventory/products',
  INVENTORY_IMPORT_PRODUCTS: '/inventory/import-products',
  INVENTORY_BARCODE_GENERATE: '/inventory/barcode-generate',
  INVENTORY_TAXES: '/inventory/taxes',
  INVENTORY_UNITS: '/inventory/units',
  INVENTORY_STOCK_TRANSFER: '/inventory/stock-transfer',
  INVENTORY_STOCK_TRANSFER_CREATE: '/inventory/stock-transfer/create',
  INVENTORY_PHYSICAL_STOCK_UPDATE: '/inventory/physical-stock-update',
  PHYSICAL_STOCK_MANAGEMENT: '/inventory/physical-stock-entry',
  PHYSICAL_STOCK_ENTRY: '/inventory/physical-stock-entry',
  PHYSICAL_STOCK_VIEW: '/inventory/physical-stock-update',
  LOCATION_MANAGEMENT: '/location-management',
  LOCATION_MANAGEMENT_LOCATIONS: '/inventory/locations',
  LOCATION_MANAGEMENT_USERS: '/inventory/location-users',
  LOCATION_MANAGEMENT_STOCK_TRANSFER: '/inventory/stock-transfer',
  SALES: '/sales',
  SALES_ORDERS: '/sales/pos',
  SALES_POS_KEYBOARD: '/sales/pos-keyboard',
  SALES_VIEW_ORDERS: '/sales/view-orders',
  SALES_RETURNS: '/sales/returns',
  SALES_VIEW_RETURNS: '/sales/view-returns',
  PURCHASES: '/purchases',
  PURCHASES_ORDERS: '/purchases/orders',
  PURCHASES_PO_KEYBOARD: '/purchases/po-keyboard',
  PURCHASES_VIEW_ORDERS: '/purchases/view-orders',
  PURCHASES_RETURNS: '/purchases/returns',
  PEOPLE: '/people',
  PEOPLE_CUSTOMERS: '/people/customers',
  PEOPLE_SUPPLIERS: '/people/suppliers',
  PEOPLE_USERS: '/people/users',
  REPORTS: '/reports',
  REPORTS_SALES: '/reports/sales',
  REPORTS_LOCATION_SALES: '/reports/location-sales',
  REPORTS_TOP_SELLING_PRODUCTS: '/reports/top-selling-products',
  REPORTS_PURCHASES: '/reports/purchases',
  REPORTS_INVENTORY: '/reports/inventory',
  REPORTS_STOCK: '/reports/stock',
  REPORTS_STOCK_MATRIX: '/reports/stock-matrix',
  REPORTS_DAY_CLOSE: '/reports/day-close',
  REPORTS_STOCK_TRANSFER: '/reports/stock-transfer',
  REPORTS_PRODUCTS: '/reports/products',
  REPORTS_BILLING: '/reports/billing',
  REPORTS_INVOICE_PROFIT_LOSS: '/reports/invoice-profit-loss',
  REPORTS_CREDIT_CUSTOMERS: '/reports/credit-customers',
  REPORTS_GST: '/reports/gst',
  REPORTS_HSN: '/reports/hsn',
  PRIVILEGES: '/privileges',
  ROLE_PRIVILEGES: '/role-privileges',
  EXPENSES: '/expenses',
  EXPENSES_ENTRY: '/expenses/entry',
  EXPENSES_VIEW: '/expenses/view',
  SETTINGS: '/settings',
  SETTINGS_COMPANY: '/settings/company',
  SETTINGS_PRINT: '/settings/print',
  SETTINGS_LABEL: '/settings/label-settings',
  PROFILE: '/profile'
};

// Default privileges for each role if none are defined
const DEFAULT_PRIVILEGES = {
  [ROLES.ADMIN]: {
    // Admin has access to everything
    visibleMenus: Object.keys(MENUS),
    accessibleRoutes: Object.values(MENUS).concat(
      ['/sales/*', '/purchases/*', '/inventory/*', '/people/*', '/reports/*', '/role-privileges', '/inventory/physical-stock-entry']
    )
  },
  [ROLES.MANAGER]: {
    visibleMenus: [
      'DASHBOARD', 'INVENTORY', 'INVENTORY_CATEGORIES', 'INVENTORY_PRODUCTS', 'INVENTORY_BARCODE_GENERATE',
      'INVENTORY_TAXES', 'INVENTORY_UNITS', 'INVENTORY_PHYSICAL_STOCK_UPDATE', 'LOCATION_MANAGEMENT', 'LOCATION_MANAGEMENT_LOCATIONS', 'LOCATION_MANAGEMENT_USERS', 'LOCATION_MANAGEMENT_STOCK_TRANSFER', 'SALES', 'SALES_ORDERS',
      'SALES_VIEW_ORDERS', 'SALES_RETURNS', 'SALES_VIEW_RETURNS',
      'PURCHASES', 'PURCHASES_ORDERS', 'PURCHASES_PO_KEYBOARD', 'PURCHASES_VIEW_ORDERS', 'PURCHASES_RETURNS',
      'EXPENSES', 'EXPENSES_ENTRY', 'EXPENSES_VIEW',
      'PEOPLE', 'PEOPLE_CUSTOMERS', 'PEOPLE_SUPPLIERS',
      'REPORTS', 'REPORTS_SALES', 'REPORTS_PURCHASES', 'REPORTS_INVENTORY',
      'REPORTS_STOCK', 'REPORTS_STOCK_MATRIX', 'REPORTS_PRODUCTS', 'REPORTS_BILLING', 'REPORTS_GST', 'REPORTS_HSN',
      'SETTINGS', 'SETTINGS_COMPANY', 'SETTINGS_PRINT', 'SETTINGS_LABEL'
    ],
    accessibleRoutes: [
      '/dashboard', '/inventory', '/inventory/*', '/inventory/barcode-generate', '/inventory/physical-stock-update', '/location-management', '/inventory/locations', '/inventory/location-users', '/sales', '/sales/*',
      '/purchases', '/purchases/*', '/people/customers', '/people/suppliers',
      '/expenses', '/expenses/entry', '/expenses/view',
      '/reports', '/reports/*',
      '/settings', '/settings/*',
      '/profile'
    ]
  },
  [ROLES.EXECUTIVE]: {
    visibleMenus: [
      'DASHBOARD',
      'SALES', 'SALES_ORDERS', 'SALES_VIEW_ORDERS', 'SALES_RETURNS', 'SALES_VIEW_RETURNS',
      'PEOPLE', 'PEOPLE_CUSTOMERS',
      'INVENTORY', 'INVENTORY_PRODUCTS', 'INVENTORY_BARCODE_GENERATE',
      'REPORTS', 'REPORTS_SALES', 'REPORTS_PRODUCTS',
      'SETTINGS', 'SETTINGS_PRINT', 'SETTINGS_LABEL'
    ],
    accessibleRoutes: [
      '/dashboard',
      '/sales', '/sales/pos', '/sales/view-orders', '/sales/returns', '/sales/view-returns', '/sales/*',
      '/people', '/people/customers',
      '/inventory/products', '/inventory/barcode-generate',
      '/reports/sales', '/reports/products',
      '/settings', '/settings/*',
      '/profile'
    ]
  },
  [ROLES.BILLING_PERSON]: {
    visibleMenus: [
      'DASHBOARD', 'SALES', 'SALES_ORDERS', 'SALES_VIEW_ORDERS',
      'REPORTS', 'REPORTS_SALES', 'REPORTS_BILLING', 'REPORTS_PRODUCTS',
      'SETTINGS', 'SETTINGS_PRINT'
    ],
    accessibleRoutes: [
      '/dashboard', '/sales/pos', '/sales/view-orders',
      '/reports/sales', '/reports/billing', '/reports/products',
      '/settings', '/settings/*',
      '/profile'
    ]
  },
  [ROLES.USER]: {
    visibleMenus: ['DASHBOARD'],
    accessibleRoutes: ['/dashboard', '/profile']
  }
};

/**
 * Get the privileges for a specific role
 * @param {string} role - The role to get privileges for
 * @returns {Object} The privileges for the role
 */
export const getRolePrivileges = (role) => {
  // Admin always has full access
  if (role === ROLES.ADMIN) {
    return DEFAULT_PRIVILEGES[ROLES.ADMIN];
  }

  try {
    // CRITICAL: ONLY get privileges from localStorage (which should be synced from backend)
    // DO NOT use frontend defaults - pages should ONLY be shown based on backend response
    const storedPrivileges = localStorage.getItem(`privileges_${role}`);

    console.log(`[getRolePrivileges] Role: ${role}, Stored: ${storedPrivileges ? 'YES' : 'NO'}`);

    if (storedPrivileges) {
      const parsed = JSON.parse(storedPrivileges);
      console.log(`[getRolePrivileges] Parsed privileges from localStorage:`, parsed);
      console.log(`[getRolePrivileges] Visible menus: ${parsed.visibleMenus?.length || 0}, Accessible routes: ${parsed.accessibleRoutes?.length || 0}`);
      return parsed;
    }

    // CRITICAL: If no stored privileges, return EMPTY (deny all access)
    // This forces backend sync to happen - pages should ONLY be shown based on backend response
    console.warn(`[getRolePrivileges] ⚠️⚠️⚠️ NO PRIVILEGES FOUND IN LOCALSTORAGE FOR ROLE ${role}`);
    console.warn(`[getRolePrivileges] ⚠️⚠️⚠️ This means syncPrivilegesFromBackend hasn't run yet or failed`);
    console.warn(`[getRolePrivileges] ⚠️⚠️⚠️ Returning EMPTY privileges - pages will be hidden until backend sync`);

    // Return empty privileges - this will hide all pages until backend sync happens
    return { visibleMenus: [], accessibleRoutes: [] };
  } catch (error) {
    console.error('[getRolePrivileges] Error getting role privileges:', error);
    // Return empty privileges on error - deny all access
    return { visibleMenus: [], accessibleRoutes: [] };
  }
};

/**
 * Save privileges for a specific role
 * @param {string} role - The role to save privileges for
 * @param {Object} privileges - The privileges to save
 * @returns {boolean} Whether the save was successful
 */
export const saveRolePrivileges = (role, privileges) => {
  try {
    localStorage.setItem(`privileges_${role}`, JSON.stringify(privileges));
    return true;
  } catch (error) {
    console.error('Error saving role privileges:', error);
    return false;
  }
};

/**
 * Check if the current user has access to a given route
 * @param {string} route - The route to check access for
 * @returns {boolean} Whether the user has access to the route
 */
export const hasRouteAccess = (route) => {
  try {
    const role = getUserRole();

    console.log(`\n========== [hasRouteAccess] DEBUG START ==========`);
    console.log(`[hasRouteAccess] Route: "${route}"`);
    console.log(`[hasRouteAccess] Role: "${role}"`);

    // Admin has access to everything
    if (role === ROLES.ADMIN) {
      console.log(`[hasRouteAccess] Admin user - granting access`);
      return true;
    }

    // Get privileges for the current role
    const rolePrivileges = getRolePrivileges(role);
    console.log(`[hasRouteAccess] Role privileges object:`, rolePrivileges);

    const { accessibleRoutes = [] } = rolePrivileges || {};
    console.log(`[hasRouteAccess] Accessible routes (${accessibleRoutes.length}):`, accessibleRoutes);

    // Get submenu-specific permissions
    const submenuPermissions = getSubmenuPermissions(role);
    console.log(`[hasRouteAccess] Submenu permissions (${submenuPermissions.length}):`, submenuPermissions);

    // Debug: Check if submenu permissions exist in localStorage
    const submenuKey = `submenu_permissions_${role}`;
    const rawSubmenuData = localStorage.getItem(submenuKey);
    console.log(`[hasRouteAccess] Raw submenu data from localStorage key "${submenuKey}":`, rawSubmenuData);
    if (!rawSubmenuData) {
      console.warn(`[hasRouteAccess] ⚠️⚠️⚠️ NO SUBMENU PERMISSIONS IN LOCALSTORAGE!`);
      console.warn(`[hasRouteAccess] ⚠️⚠️⚠️ This means syncPrivilegesFromBackend may not have run or failed!`);
    }

    const submenuMap = submenuPermissions.reduce((map, submenu) => {
      map[submenu.id] = submenu;
      return map;
    }, {});
    console.log(`[hasRouteAccess] Submenu map:`, submenuMap);
    console.log(`[hasRouteAccess] Submenu map keys:`, Object.keys(submenuMap));

    // CRITICAL FIX: Handle wildcard route patterns (e.g., "/sales/*")
    // When React Router matches a route pattern, it passes the pattern itself
    // We need to check if any routes under that pattern are accessible
    if (route.endsWith('/*')) {
      const basePath = route.slice(0, -2); // Remove '/*'
      console.log(`[hasRouteAccess] Route is a wildcard pattern: ${route}, base: ${basePath}`);

      // Check if we have any accessible routes under this base path
      const routesUnderBase = accessibleRoutes.filter(r =>
        r.startsWith(basePath + '/') || r === basePath
      );

      console.log(`[hasRouteAccess] Routes under ${basePath}:`, routesUnderBase);

      if (routesUnderBase.length > 0) {
        // We have specific routes under this base - check submenu permissions if they exist
        if (submenuPermissions.length > 0) {
          const moduleName = basePath.replace('/', '').toUpperCase();
          const moduleHasSubmenuPermissions = Object.keys(submenuMap).some(id =>
            id.startsWith(moduleName + '_')
          );

          if (moduleHasSubmenuPermissions) {
            // Module has submenu permissions - allow access to the wildcard pattern
            // Individual routes will be checked when they're actually accessed
            console.log(`[hasRouteAccess] ✓ Wildcard pattern ${route} allowed - module has submenu permissions`);
            console.log(`[hasRouteAccess] Individual routes will be checked when accessed`);
            console.log(`========== [hasRouteAccess] DEBUG END (ALLOWED) ==========\n`);
            return true;
          }
        }

        // No submenu permissions or module doesn't have them - allow based on routes
        console.log(`[hasRouteAccess] ✓ Wildcard pattern ${route} allowed - has ${routesUnderBase.length} accessible routes`);
        console.log(`========== [hasRouteAccess] DEBUG END (ALLOWED) ==========\n`);
        return true;
      } else {
        // No routes under this base - deny access
        console.log(`[hasRouteAccess] ✗ Wildcard pattern ${route} denied - no accessible routes under ${basePath}`);
        console.log(`========== [hasRouteAccess] DEBUG END (DENIED) ==========\n`);
        return false;
      }
    }

    // CRITICAL FIX: If we have submenu permissions, we MUST check them even for exact route matches
    // This prevents access to routes that match wildcards but don't have specific permissions
    if (submenuPermissions.length > 0) {
      // Check if this route has a specific submenu permission
      const submenuId = Object.entries(MENUS).find(([key, path]) =>
        path === route
      )?.[0];

      if (submenuId) {
        // Check if this specific submenu has permission
        if (submenuMap[submenuId]) {
          const hasAccess = submenuMap[submenuId].canView;
          console.log(`[hasRouteAccess] Submenu permission check for exact match: ${submenuId}.canView = ${hasAccess}`);
          if (hasAccess) {
            console.log(`[hasRouteAccess] ✓ Exact match with submenu permission found for route: ${route}`);
            console.log(`========== [hasRouteAccess] DEBUG END (ALLOWED) ==========\n`);
            return true;
          } else {
            console.log(`[hasRouteAccess] ✗ Exact match but submenu permission denied for route: ${route}`);
            console.log(`========== [hasRouteAccess] DEBUG END (DENIED) ==========\n`);
            return false;
          }
        } else {
          // Route exists in accessibleRoutes but no submenu permission - check if module has submenu permissions
          const moduleName = route.split('/')[1]?.toUpperCase();
          const moduleHasSubmenuPermissions = Object.keys(submenuMap).some(id =>
            id.startsWith(moduleName + '_')
          );

          if (moduleHasSubmenuPermissions) {
            console.log(`[hasRouteAccess] ⚠️⚠️⚠️ Route ${route} has no submenu permission but module ${moduleName} has submenu permissions - DENYING`);
            console.log(`========== [hasRouteAccess] DEBUG END (DENIED) ==========\n`);
            return false;
          }
        }
      }
    }

    // CRITICAL FIX: Handle redirect routes
    // /sales/customers redirects to /people/customers, so check if user has access to the target route
    const redirectRouteMap = {
      '/sales/customers': '/people/customers',
      '/purchases/suppliers': '/people/suppliers'
    };

    if (redirectRouteMap[route]) {
      const targetRoute = redirectRouteMap[route];
      console.log(`[hasRouteAccess] Route ${route} is a redirect to ${targetRoute}, checking target route access`);
      if (accessibleRoutes.includes(targetRoute)) {
        console.log(`[hasRouteAccess] ✓ Redirect route ${route} allowed (target ${targetRoute} is accessible)`);
        console.log(`========== [hasRouteAccess] DEBUG END (ALLOWED) ==========\n`);
        return true;
      }
    }

    // First, check exact route match (only if no submenu permissions or permission check passed)
    if (accessibleRoutes.includes(route)) {
      console.log(`[hasRouteAccess] ✓ Exact match found for route: ${route}`);
      console.log(`========== [hasRouteAccess] DEBUG END (ALLOWED) ==========\n`);
      return true;
    }

    // CRITICAL FIX: Check wildcard paths ONLY if we have submenu permissions OR if the route is explicitly in accessibleRoutes
    // If submenu permissions are empty, we should NOT allow wildcard access - only exact matches
    for (const accessibleRoute of accessibleRoutes) {
      if (accessibleRoute.endsWith('/*')) {
        const basePath = accessibleRoute.slice(0, -2); // Remove '/*'
        console.log(`[hasRouteAccess] Checking wildcard: ${accessibleRoute} (base: ${basePath})`);

        // Check if route starts with basePath (e.g., /sales/pos starts with /sales)
        if (route.startsWith(basePath + '/') || route === basePath) {
          console.log(`[hasRouteAccess] Route ${route} matches wildcard ${accessibleRoute}`);

          // CRITICAL: If we have NO submenu permissions, DO NOT allow wildcard access
          // Pages should ONLY be accessible if they're explicitly in accessibleRoutes (from database)
          if (submenuPermissions.length === 0) {
            console.log(`[hasRouteAccess] ⚠️⚠️⚠️ NO SUBMENU PERMISSIONS - Wildcard access DENIED`);
            console.log(`[hasRouteAccess] ⚠️⚠️⚠️ Route ${route} must be explicitly in accessibleRoutes to be allowed`);
            console.log(`[hasRouteAccess] ⚠️⚠️⚠️ This ensures pages are ONLY shown based on database entries`);
            // Continue to check if route is explicitly in accessibleRoutes (exact match)
            continue; // Don't allow based on wildcard, check exact match below
          }

          // If we have submenu permissions, check them
          const submenuId = Object.entries(MENUS).find(([key, path]) =>
            path === route
          )?.[0];

          console.log(`[hasRouteAccess] Submenu ID for route ${route}:`, submenuId);

          // Check if this module has any submenu permissions
          const moduleName = basePath.replace('/', '').toUpperCase(); // e.g., SALES, REPORTS
          const moduleHasSubmenuPermissions = Object.keys(submenuMap).some(id =>
            id.startsWith(moduleName + '_')
          );

          console.log(`[hasRouteAccess] Module ${moduleName} has submenu permissions:`, moduleHasSubmenuPermissions);
          console.log(`[hasRouteAccess] Submenu permissions for ${moduleName}:`, Object.keys(submenuMap).filter(id => id.startsWith(moduleName + '_')));

          if (moduleHasSubmenuPermissions) {
            console.log(`[hasRouteAccess] ⚠️ Module ${moduleName} has specific submenu permissions - MUST check them`);
            // This module has specific submenu permissions - we MUST check them
            if (submenuId && submenuMap[submenuId]) {
              // Specific submenu permission exists - use it
              const hasAccess = submenuMap[submenuId].canView;
              console.log(`[hasRouteAccess] Submenu permission check: ${submenuId}.canView = ${hasAccess}`);
              console.log(`========== [hasRouteAccess] DEBUG END (${hasAccess ? 'ALLOWED' : 'DENIED'}) ==========\n`);
              return hasAccess;
            } else {
              // Module has submenu permissions but this specific route doesn't have one - DENY
              console.log(`[hasRouteAccess] ⚠️⚠️⚠️ Module has submenu permissions but route ${route} has no specific permission - DENYING`);
              console.log(`[hasRouteAccess] ⚠️⚠️⚠️ This prevents access to unauthorized pages like /reports/purchases`);
              console.log(`========== [hasRouteAccess] DEBUG END (DENIED) ==========\n`);
              return false;
            }
          }

          // If we have submenu permissions but this module doesn't have specific ones, allow based on wildcard
          // (This is a fallback for modules that don't have granular permissions)
          console.log(`[hasRouteAccess] Module has submenu permissions but not for this module, allowing based on wildcard`);
          console.log(`========== [hasRouteAccess] DEBUG END (ALLOWED) ==========\n`);
          return true;
        }
      }
    }

    // Also check if route matches any submenu path directly (for cases where submenu permissions exist)
    const submenuId = Object.entries(MENUS).find(([key, path]) =>
      path === route
    )?.[0];

    console.log(`[hasRouteAccess] Direct submenu lookup for route ${route}:`, submenuId);

    if (submenuId && submenuMap[submenuId]) {
      const hasAccess = submenuMap[submenuId].canView;
      console.log(`[hasRouteAccess] ✓ Direct submenu check: ${submenuId}.canView = ${hasAccess}`);
      console.log(`========== [hasRouteAccess] DEBUG END (${hasAccess ? 'ALLOWED' : 'DENIED'}) ==========\n`);
      return hasAccess;
    }

    console.log(`[hasRouteAccess] ✗ No access found for route: ${route}`);
    console.log(`[hasRouteAccess] Available routes:`, accessibleRoutes);
    console.log(`[hasRouteAccess] Available submenu IDs:`, Object.keys(submenuMap));
    console.log(`========== [hasRouteAccess] DEBUG END (DENIED) ==========\n`);
    return false;
  } catch (error) {
    console.error('[hasRouteAccess] Error checking route access:', error);
    console.error('[hasRouteAccess] Stack:', error.stack);
    return false;
  }
};

/**
 * Check if the current user has access to a specific API endpoint
 * @param {string} url - The API endpoint to check
 * @returns {boolean} Whether the user has access
 */
export const hasApiAccess = (url) => {
  // Skip permission check for auth-related endpoints
  if (url.includes('/auth/') || url.includes('/login')) {
    return true;
  }

  const role = getUserRole();

  // Admin has access to everything
  if (role === ROLES.ADMIN) {
    return true;
  }

  // CRITICAL: Allow users to fetch their own role privileges
  // This is needed for the privilege sync to work
  if (url.includes('/role-privileges/')) {
    return true;
  }

  // Allow trial/subscription status and creation calls
  // Handle both with and without /api prefix, and with query parameters
  if (url.includes('/company-subscriptions/') || url.includes('company-subscriptions')) {
    return true;
  }

  // Get privileges for the current role
  const { accessibleRoutes = [] } = getRolePrivileges(role) || {};

  // Allow usage/analytics endpoints for dashboard (non-critical data)
  if (url.includes('/usage') || url.includes('/analytics')) {
    // Only allow if user has dashboard route access
    return accessibleRoutes.some(route =>
      route === '/dashboard' || route.startsWith('/dashboard')
    );
  }

  // Allow dashboard service endpoints
  if (url.includes('/dashboard-service') || url.includes('/dashboard/')) {
    // Only allow if user has dashboard route access
    return accessibleRoutes.some(route =>
      route === '/dashboard' || route.startsWith('/dashboard')
    );
  }

  // Check dashboard API access based on route privileges (not hardcoded)
  if (url.includes('/dashboard') || url.includes('/analytics') || url.includes('/location-dashboard')) {
    // Only allow if user has dashboard route access
    return accessibleRoutes.some(route =>
      route === '/dashboard' || route.startsWith('/dashboard')
    );
  }

  // Map API endpoint to frontend routes
  const apiPathSegments = url.split('/').filter(Boolean);
  let resourceType = '';

  if (apiPathSegments.length > 0) {
    // Skip 'api' prefix if present
    const startIndex = apiPathSegments[0] === 'api' ? 1 : 0;

    if (apiPathSegments.length > startIndex) {
      resourceType = (apiPathSegments[startIndex] || '').split('?')[0];
    }
  }

  // Add specific check for products - executives need to view products
  if (resourceType === 'products' && role === ROLES.EXECUTIVE) {
    return true;
  }

  // Resource-specific checks
  switch (resourceType) {
    case 'sales':
    case 'sales-orders':
      return accessibleRoutes.some(route =>
        route.startsWith('/sales') || route === '/sales' || route === '/sales/*'
      );

    case 'purchases':
    case 'purchase-orders':
      return accessibleRoutes.some(route =>
        route.startsWith('/purchases') || route === '/purchases' || route === '/purchases/*'
      );

    case 'inventory':
    case 'products':
    case 'categories':
    case 'taxes':
    case 'units':
    case 'stock-transfer':
    case 'physical-stock':
      return accessibleRoutes.some(route =>
        route.startsWith('/inventory') || route === '/inventory' || route === '/inventory/*'
      );

    case 'people':
    case 'users':
    case 'suppliers':
    case 'salesmen':
      return accessibleRoutes.some(route =>
        route.startsWith('/people') || route === '/people' || route === '/people/*'
      );

    case 'customers':
      // Allow customer API access if user has:
      // 1. Access to people/customers page (for customer management)
      // 2. Access to sales pages (for POS/order creation - customers needed for sales)
      const hasPeopleAccess = accessibleRoutes.some(route =>
        route.startsWith('/people') || route === '/people' || route === '/people/*'
      );
      const hasSalesAccess = accessibleRoutes.some(route =>
        route.startsWith('/sales') || route === '/sales' || route === '/sales/*'
      );
      return hasPeopleAccess || hasSalesAccess;

    case 'reports':
      return accessibleRoutes.some(route =>
        route.startsWith('/reports') || route === '/reports' || route === '/reports/*'
      );

    case 'print-settings':
    case 'company-settings':
    case 'activity-log':
      return accessibleRoutes.some(route =>
        route.startsWith('/settings') || route === '/settings' || route === '/settings/*'
      );

    default:
      return false;
  }
};

/**
 * Get the visible menu items for the current user
 * @returns {Array} The visible menu IDs
 */
export const getVisibleMenuItems = () => {
  try {
    const role = getUserRole();

    // Admin sees everything
    if (role === ROLES.ADMIN) {
      return Object.keys(MENUS);
    }

    // CRITICAL: Get privileges ONLY from localStorage (which should be synced from backend)
    // DO NOT use any frontend defaults
    const rolePrivileges = getRolePrivileges(role);
    const { visibleMenus = [] } = rolePrivileges || {};

    console.log(`[getVisibleMenuItems] Role: ${role}, Visible menus from backend: ${visibleMenus.length}`);

    // If no visible menus, return empty (backend hasn't synced or no privileges in DB)
    if (visibleMenus.length === 0) {
      console.warn(`[getVisibleMenuItems] ⚠️⚠️⚠️ NO VISIBLE MENUS - Backend sync may not have completed`);
      console.warn(`[getVisibleMenuItems] ⚠️⚠️⚠️ Returning empty array - no pages will be shown`);
      return [];
    }

    // Get submenu-specific permissions from localStorage (synced from backend)
    const submenuPermissions = getSubmenuPermissions(role);

    // If we have submenu permissions, filter based on them (from backend)
    if (submenuPermissions.length > 0) {
      console.log(`[getVisibleMenuItems] Filtering ${visibleMenus.length} menus using ${submenuPermissions.length} submenu permissions from backend`);

      // Create a lookup map for quick access
      const submenuMap = submenuPermissions.reduce((map, submenu) => {
        map[submenu.id] = submenu.canView;
        return map;
      }, {});

      // Filter visible menus based on submenu permissions (from backend)
      const filtered = visibleMenus.filter(menuId => {
        // For submenus, check the specific permission
        if (submenuMap.hasOwnProperty(menuId)) {
          return submenuMap[menuId];
        }

        // For main menus, check if any of their submenus are visible
        if (['INVENTORY', 'SALES', 'PURCHASES', 'REPORTS', 'PEOPLE'].includes(menuId)) {
          // Get submenu IDs for this menu
          const submenus = Object.keys(MENUS).filter(id =>
            id.startsWith(menuId + '_') ||
            (menuId === 'PEOPLE' && id.startsWith('PEOPLE_'))
          );

          // Check if any submenu is visible (based on backend permissions)
          return submenus.some(submenuId => submenuMap[submenuId]);
        }

        // For other menus, check if they're in visibleMenus (from backend)
        return visibleMenus.includes(menuId);
      });

      console.log(`[getVisibleMenuItems] Filtered to ${filtered.length} visible menus based on backend permissions`);
      return filtered;
    }

    // No submenu permissions - return visibleMenus as-is (from backend)
    console.log(`[getVisibleMenuItems] Returning ${visibleMenus.length} visible menus from backend (no submenu filtering)`);
    return visibleMenus;
  } catch (error) {
    console.error('[getVisibleMenuItems] Error getting visible menu items:', error);
    // Return empty on error - deny all access
    return [];
  }
};

/**
 * Fix existing privileges by removing wildcard routes when submenu permissions exist
 * This prevents access to unauthorized pages when only specific submenu privileges exist
 */
export const fixPrivilegeRoutes = (role) => {
  try {
    const storedPrivileges = localStorage.getItem(`privileges_${role}`);
    if (!storedPrivileges) return;

    const privileges = JSON.parse(storedPrivileges);
    const { accessibleRoutes = [] } = privileges;
    let updated = false;

    // Check if we have submenu permissions for this role
    const submenuPermissions = getSubmenuPermissions(role);
    console.log(`[fixPrivilegeRoutes] Submenu permissions found: ${submenuPermissions.length}`);

    if (submenuPermissions.length > 0) {
      // We have submenu permissions - identify which modules have them
      const modulesWithSubmenuPermissions = new Set();
      submenuPermissions.forEach(perm => {
        const module = perm.id.split('_')[0];
        modulesWithSubmenuPermissions.add(module);
      });

      console.log(`[fixPrivilegeRoutes] Modules with submenu permissions:`, Array.from(modulesWithSubmenuPermissions));

      // Remove wildcard routes for modules that have submenu permissions
      const routesToRemove = [];
      accessibleRoutes.forEach(route => {
        if (route.endsWith('/*')) {
          const basePath = route.slice(0, -2); // Remove '/*'
          const moduleName = basePath.replace('/', '').toUpperCase();

          if (modulesWithSubmenuPermissions.has(moduleName)) {
            routesToRemove.push(route);
            console.log(`[fixPrivilegeRoutes] ⚠️⚠️⚠️ REMOVING wildcard ${route} - module ${moduleName} has specific submenu permissions`);
          }
        }
      });

      // Remove the wildcard routes
      routesToRemove.forEach(route => {
        const index = accessibleRoutes.indexOf(route);
        if (index > -1) {
          accessibleRoutes.splice(index, 1);
          updated = true;
        }
      });
    } else {
      // No submenu permissions found - add wildcards for navigation if we have specific routes
      // This ensures React Router can match route patterns
      const routeGroups = {};
      accessibleRoutes.forEach(route => {
        if (route.startsWith('/') && route !== '/dashboard' && !route.endsWith('/*')) {
          const parts = route.split('/').filter(p => p);
          if (parts.length >= 2) {
            const parentPath = `/${parts[0]}`;
            if (!routeGroups[parentPath]) {
              routeGroups[parentPath] = [];
            }
            routeGroups[parentPath].push(route);
          }
        }
      });

      // Add parent paths and wildcards for each group
      Object.keys(routeGroups).forEach(parentPath => {
        if (!accessibleRoutes.includes(parentPath)) {
          accessibleRoutes.push(parentPath);
          updated = true;
          console.log(`[fixPrivilegeRoutes] Added parent route: ${parentPath}`);
        }
        const wildcardPath = `${parentPath}/*`;
        if (!accessibleRoutes.includes(wildcardPath)) {
          accessibleRoutes.push(wildcardPath);
          updated = true;
          console.log(`[fixPrivilegeRoutes] Added wildcard route: ${wildcardPath} (for navigation)`);
        }
      });
    }

    if (updated) {
      privileges.accessibleRoutes = accessibleRoutes;
      localStorage.setItem(`privileges_${role}`, JSON.stringify(privileges));
      console.log(`[fixPrivilegeRoutes] Fixed routes for role ${role}:`, accessibleRoutes);
    }
  } catch (error) {
    console.error('[fixPrivilegeRoutes] Error fixing routes:', error);
  }
};

/**
 * Initialize privileges system
 * This should be called when the app starts
 * IMPORTANT: This does NOT clear existing privileges to preserve admin-set custom privileges
 */
export const initializePrivileges = () => {
  // CRITICAL: DO NOT initialize default privileges
  // Privileges should ONLY come from backend via syncPrivilegesFromBackend
  // This ensures pages are ONLY shown based on database entries, not frontend defaults
  console.log('[initializePrivileges] Initializing - privileges will come from backend sync only');

  // Only fix existing routes if privileges exist (they should be from backend)
  Object.keys(ROLES).forEach(role => {
    if (role === ROLES.ADMIN) {
      // Admin always has full access
      if (!localStorage.getItem(`privileges_${role}`)) {
        localStorage.setItem(
          `privileges_${role}`,
          JSON.stringify(DEFAULT_PRIVILEGES[ROLES.ADMIN])
        );
      }
    } else {
      // For other roles, only fix routes if privileges exist (from backend)
      // DO NOT create default privileges - they must come from backend
      const storedPrivileges = localStorage.getItem(`privileges_${role}`);
      if (storedPrivileges) {
        console.log(`[initializePrivileges] Fixing routes for role ${role} (privileges exist from backend)`);
        fixPrivilegeRoutes(role);
      } else {
        console.log(`[initializePrivileges] No privileges for role ${role} - will be synced from backend on login`);
      }
    }
  });

  console.log('Privileges system initialized - pages will ONLY be shown based on backend response');
};

/**
 * Fetch privileges from backend and sync to localStorage
 * This should be called on login to ensure privileges are up-to-date
 */
export const syncPrivilegesFromBackend = async (role, forceRefresh = false) => {
  try {
    console.log(`\n========== [syncPrivilegesFromBackend] DEBUG START ==========`);
    console.log(`[syncPrivilegesFromBackend] Role: ${role}`);
    console.log(`[syncPrivilegesFromBackend] Force refresh: ${forceRefresh}`);

    if (!role || role === ROLES.ADMIN) {
      // Admin doesn't need backend sync, always has full access
      console.log(`[syncPrivilegesFromBackend] Admin role - skipping sync`);
      return;
    }

    // CRITICAL: Always fetch from backend to ensure we have latest data from database
    // This API call MUST happen to verify the system is working based on backend response
    console.log(`[syncPrivilegesFromBackend] 🔄 Making API call to backend...`);
    console.log(`[syncPrivilegesFromBackend] 📡 API: GET /api/role-privileges/${role}`);

    // Try to fetch from backend
    const axios = (await import('../utils/axios')).default;
    const response = await axios.get(`/role-privileges/${role}`);

    console.log(`[syncPrivilegesFromBackend] ✅ Backend API response received`);
    console.log(`[syncPrivilegesFromBackend] Response status: ${response.status}`);
    console.log(`[syncPrivilegesFromBackend] Response data:`, JSON.stringify(response.data, null, 2));
    console.log(`[syncPrivilegesFromBackend] Number of privileges in response: ${response.data?.privileges?.length || 0}`);

    if (response.data && response.data.privileges) {
      // Convert backend format to frontend format
      const visibleMenus = [];
      const accessibleRoutes = [];
      const submenuPermissions = [];

      // Map submenu IDs to their paths
      const submenuIdToPath = {};
      Object.entries(MENUS).forEach(([key, path]) => {
        submenuIdToPath[key] = path;
      });

      console.log(`[syncPrivilegesFromBackend] Processing ${response.data.privileges.length} privileges`);

      // CRITICAL FIX: First, identify which modules have specific submenu privileges
      // If a module has specific submenu privileges, we should IGNORE module-level privileges for that module
      // Map backend module names to frontend module names (CUSTOMERS/SUPPLIERS/USERS -> PEOPLE)
      const backendToFrontendModuleMap = {
        'CUSTOMERS': 'PEOPLE',
        'SUPPLIERS': 'PEOPLE',
        'USERS': 'PEOPLE'
      };

      const modulesWithSubmenuPrivileges = new Set();
      response.data.privileges.forEach(privilege => {
        if (privilege.submenuId && privilege.canView) {
          // Extract module from submenuId (e.g., SALES_ORDERS -> SALES, PEOPLE_CUSTOMERS -> PEOPLE)
          const moduleFromSubmenu = privilege.submenuId.split('_')[0];

          // Also check backend module name and map if needed
          // Backend may return module: "CUSTOMERS" with submenuId: "PEOPLE_CUSTOMERS"
          // We need to map CUSTOMERS -> PEOPLE for consistency
          const backendModule = privilege.module;
          const frontendModule = backendToFrontendModuleMap[backendModule] || moduleFromSubmenu;

          modulesWithSubmenuPrivileges.add(frontendModule);
          // Also add the backend module name if it's different, so we can check both
          if (backendModule !== frontendModule) {
            modulesWithSubmenuPrivileges.add(backendModule);
          }

          console.log(`[syncPrivilegesFromBackend] Found submenu privilege for module: ${frontendModule} (backend: ${backendModule}, submenu: ${privilege.submenuId})`);
        }
      });

      console.log(`[syncPrivilegesFromBackend] ⚠️ Modules with specific submenu privileges:`, Array.from(modulesWithSubmenuPrivileges));
      console.log(`[syncPrivilegesFromBackend] ⚠️ Module-level privileges for these modules will be SKIPPED`);

      response.data.privileges.forEach((privilege, index) => {
        console.log(`\n[syncPrivilegesFromBackend] ========== Processing Privilege ${index + 1} ==========`);
        console.log(`[syncPrivilegesFromBackend] Privilege data:`, JSON.stringify(privilege, null, 2));

        if (privilege.canView) {
          if (privilege.submenuId) {
            // Submenu-level privilege
            const submenuId = privilege.submenuId;
            const submenuPath = submenuIdToPath[submenuId];

            console.log(`[syncPrivilegesFromBackend] Processing submenu: ${submenuId} -> ${submenuPath}`);

            if (submenuId && submenuPath) {
              // Add to visible menus
              if (!visibleMenus.includes(submenuId)) {
                visibleMenus.push(submenuId);
              }

              // Add parent menu if not already added
              const parentMenuId = submenuId.split('_')[0] + (submenuId.includes('_') ? '_' + submenuId.split('_').slice(1, -1).join('_') : '');
              // For SALES_ORDERS, parent is SALES
              const parentId = submenuId.substring(0, submenuId.lastIndexOf('_')) || submenuId.split('_')[0];
              if (parentId && !visibleMenus.includes(parentId)) {
                // Try to find parent menu
                const parentMenus = ['DASHBOARD', 'INVENTORY', 'LOCATION_MANAGEMENT', 'SALES', 'PURCHASES', 'PEOPLE', 'REPORTS', 'SETTINGS'];
                const foundParent = parentMenus.find(p => submenuId.startsWith(p));
                if (foundParent && !visibleMenus.includes(foundParent)) {
                  visibleMenus.push(foundParent);
                }
              }

              // Add to accessible routes
              if (submenuPath && !accessibleRoutes.includes(submenuPath)) {
                accessibleRoutes.push(submenuPath);
                console.log(`[syncPrivilegesFromBackend] Added route: ${submenuPath}`);
              }

              // CRITICAL FIX: Add redirect routes for customers and suppliers
              // /sales/customers redirects to /people/customers, so we need to allow access to it
              // /purchases/suppliers redirects to /people/suppliers, so we need to allow access to it
              if (submenuId === 'PEOPLE_CUSTOMERS' && !accessibleRoutes.includes('/sales/customers')) {
                accessibleRoutes.push('/sales/customers');
                console.log(`[syncPrivilegesFromBackend] Added redirect route: /sales/customers (redirects to /people/customers)`);
              }
              if (submenuId === 'PEOPLE_SUPPLIERS' && !accessibleRoutes.includes('/purchases/suppliers')) {
                accessibleRoutes.push('/purchases/suppliers');
                console.log(`[syncPrivilegesFromBackend] Added redirect route: /purchases/suppliers (redirects to /people/suppliers)`);
              }

              // Add parent path and wildcard (CRITICAL for route matching)
              // BUT: Only add wildcard if this module doesn't have specific submenu privileges
              // If module has submenu privileges, we should NOT add wildcard to prevent access to all pages
              const pathParts = submenuPath.split('/').filter(p => p); // Remove empty strings
              if (pathParts.length >= 2) {
                const parentPath = `/${pathParts[0]}`; // e.g., /sales or /reports
                const moduleName = pathParts[0].toUpperCase(); // e.g., SALES or REPORTS
                const hasSubmenuPrivileges = modulesWithSubmenuPrivileges.has(moduleName);

                // Always add parent path for navigation
                if (!accessibleRoutes.includes(parentPath)) {
                  accessibleRoutes.push(parentPath);
                  console.log(`[syncPrivilegesFromBackend] Added parent route: ${parentPath}`);
                }

                // CRITICAL FIX: Only add wildcard if module has NO specific submenu privileges
                // If module has submenu privileges, wildcard would grant access to ALL pages in that module
                if (!hasSubmenuPrivileges) {
                  const wildcardPath = `${parentPath}/*`; // e.g., /sales/* or /reports/*
                  if (!accessibleRoutes.includes(wildcardPath)) {
                    accessibleRoutes.push(wildcardPath);
                    console.log(`[syncPrivilegesFromBackend] Added wildcard route: ${wildcardPath} (no submenu privileges)`);
                  }
                } else {
                  console.log(`[syncPrivilegesFromBackend] ⚠️ NOT adding wildcard ${parentPath}/* - module has specific submenu privileges`);
                  console.log(`[syncPrivilegesFromBackend] ⚠️ This prevents access to unauthorized pages in ${moduleName} module`);
                }
              } else if (pathParts.length === 1) {
                // For routes like /dashboard
                const basePath = `/${pathParts[0]}`;
                if (!accessibleRoutes.includes(basePath)) {
                  accessibleRoutes.push(basePath);
                }
              }

              // Store submenu permission
              submenuPermissions.push({
                id: submenuId,
                canView: privilege.canView,
                canCreate: privilege.canCreate,
                canEdit: privilege.canEdit,
                canDelete: privilege.canDelete
              });
            } else {
              console.warn(`[syncPrivilegesFromBackend] Submenu path not found for: ${submenuId}`);
            }
          } else {
            // Module-level privilege - ONLY apply if this module has NO specific submenu privileges
            const moduleName = privilege.module;

            // Map backend module names to frontend module names for checking
            // Backend uses CUSTOMERS/SUPPLIERS/USERS, but frontend uses PEOPLE
            const backendToFrontendModuleMap = {
              'CUSTOMERS': 'PEOPLE',
              'SUPPLIERS': 'PEOPLE',
              'USERS': 'PEOPLE'
            };
            const frontendModuleName = backendToFrontendModuleMap[moduleName] || moduleName;

            // Check both backend and frontend module names
            const hasSubmenuPrivileges = modulesWithSubmenuPrivileges.has(moduleName) ||
              modulesWithSubmenuPrivileges.has(frontendModuleName);

            if (hasSubmenuPrivileges) {
              console.log(`[syncPrivilegesFromBackend] ⚠️⚠️⚠️ SKIPPING module-level privilege for ${moduleName} (frontend: ${frontendModuleName})`);
              console.log(`[syncPrivilegesFromBackend] ⚠️⚠️⚠️ Reason: Module has specific submenu privileges - using only submenu privileges`);
              console.log(`[syncPrivilegesFromBackend] ⚠️⚠️⚠️ This prevents granting access to ALL pages in ${moduleName} module`);
              return; // Skip this module-level privilege
            }

            // Module-level privilege - map to all submenus in that module
            console.log(`[syncPrivilegesFromBackend] ✓ Processing module-level: ${moduleName} (no submenu privileges found)`);
            console.log(`[syncPrivilegesFromBackend] ✓ This will grant access to ALL pages in ${moduleName} module`);

            const moduleToMenuMap = {
              'DASHBOARD': ['DASHBOARD'],
              'SALES': ['SALES', 'SALES_ORDERS', 'SALES_POS_KEYBOARD', 'SALES_VIEW_ORDERS', 'SALES_RETURNS', 'SALES_VIEW_RETURNS'],
              'PURCHASES': ['PURCHASES', 'PURCHASES_ORDERS', 'PURCHASES_PO_KEYBOARD', 'PURCHASES_VIEW_ORDERS', 'PURCHASES_RETURNS'],
              'INVENTORY': ['INVENTORY', 'INVENTORY_CATEGORIES', 'INVENTORY_PRODUCTS', 'INVENTORY_IMPORT_PRODUCTS', 'INVENTORY_TAXES', 'INVENTORY_UNITS', 'INVENTORY_STOCK_TRANSFER', 'INVENTORY_STOCK_TRANSFER_CREATE', 'INVENTORY_PHYSICAL_STOCK_UPDATE', 'LOCATION_MANAGEMENT_LOCATIONS', 'LOCATION_MANAGEMENT_USERS'],
              'REPORTS': ['REPORTS', 'REPORTS_SALES', 'REPORTS_LOCATION_SALES', 'REPORTS_TOP_SELLING_PRODUCTS', 'REPORTS_PURCHASES', 'REPORTS_INVENTORY', 'REPORTS_STOCK', 'REPORTS_STOCK_MATRIX', 'REPORTS_DAY_CLOSE', 'REPORTS_STOCK_TRANSFER', 'REPORTS_PRODUCTS', 'REPORTS_BILLING', 'REPORTS_INVOICE_PROFIT_LOSS', 'REPORTS_CREDIT_CUSTOMERS', 'REPORTS_GST', 'REPORTS_HSN'],
              'CUSTOMERS': ['PEOPLE_CUSTOMERS'],
              'SUPPLIERS': ['PEOPLE_SUPPLIERS'],
              'USERS': ['PEOPLE_USERS'],
              'SETTINGS': ['SETTINGS', 'SETTINGS_COMPANY', 'SETTINGS_PRINT']
            };

            const menuIds = moduleToMenuMap[privilege.module] || [];
            menuIds.forEach(menuId => {
              if (!visibleMenus.includes(menuId)) {
                visibleMenus.push(menuId);
              }
            });

            // Map to routes
            const moduleToRouteMap = {
              'DASHBOARD': ['/dashboard'],
              'SALES': ['/sales', '/sales/*', '/sales/pos', '/sales/pos-keyboard', '/sales/view-orders', '/sales/returns', '/sales/view-returns'],
              'PURCHASES': ['/purchases', '/purchases/*', '/purchases/orders', '/purchases/po-keyboard', '/purchases/view-orders', '/purchases/returns'],
              'INVENTORY': ['/inventory', '/inventory/*', '/inventory/categories', '/inventory/products', '/inventory/import-products', '/inventory/taxes', '/inventory/units', '/inventory/stock-transfer', '/inventory/stock-transfer/create', '/inventory/physical-stock-update', '/inventory/locations', '/inventory/location-users'],
              'REPORTS': ['/reports', '/reports/*', '/reports/sales', '/reports/location-sales', '/reports/top-selling-products', '/reports/purchases', '/reports/inventory', '/reports/stock', '/reports/stock-matrix', '/reports/day-close', '/reports/stock-transfer', '/reports/products', '/reports/billing', '/reports/invoice-profit-loss', '/reports/credit-customers'],
              'CUSTOMERS': ['/people/customers'],
              'SUPPLIERS': ['/people/suppliers'],
              'USERS': ['/people/users'],
              'SETTINGS': ['/settings', '/settings/company', '/settings/print']
            };

            const routes = moduleToRouteMap[moduleName] || [];
            routes.forEach(route => {
              if (!accessibleRoutes.includes(route)) {
                accessibleRoutes.push(route);
                console.log(`[syncPrivilegesFromBackend] ✓ Added module route: ${route}`);
              } else {
                console.log(`[syncPrivilegesFromBackend] - Route already exists: ${route}`);
              }
            });
          }
        } else {
          console.log(`[syncPrivilegesFromBackend] ✗ Privilege ${index + 1} has canView=false - SKIPPING`);
        }
        console.log(`[syncPrivilegesFromBackend] ========== End Privilege ${index + 1} ==========\n`);
      });

      // CRITICAL: Do NOT hardcode dashboard access - it must come from backend response
      // Dashboard access should be controlled by backend privileges, not hardcoded here

      // CRITICAL: Keep wildcard routes for navigation, but we'll enforce submenu permissions in hasRouteAccess
      // We need wildcards for React Router to match route patterns, but we'll check submenu permissions for actual routes
      // So we DON'T remove wildcards here - we'll handle them in hasRouteAccess
      const modulesWithSubmenuPermissions = new Set();
      submenuPermissions.forEach(perm => {
        const module = perm.id.split('_')[0];
        modulesWithSubmenuPermissions.add(module);
      });

      console.log(`[syncPrivilegesFromBackend] Modules with submenu permissions:`, Array.from(modulesWithSubmenuPermissions));
      console.log(`[syncPrivilegesFromBackend] Keeping wildcard routes for navigation - submenu permissions will be enforced in hasRouteAccess`);

      // Save to localStorage
      const privileges = { visibleMenus, accessibleRoutes };
      localStorage.setItem(`privileges_${role}`, JSON.stringify(privileges));

      // Save submenu permissions (CRITICAL - must be saved for route checking)
      if (submenuPermissions.length > 0) {
        const saved = saveSubmenuPermissions(role, submenuPermissions);
        console.log(`[syncPrivilegesFromBackend] Submenu permissions saved: ${saved ? 'YES' : 'NO'}`);
        console.log(`[syncPrivilegesFromBackend] Saved ${submenuPermissions.length} submenu permissions:`, submenuPermissions);
      } else {
        console.warn(`[syncPrivilegesFromBackend] ⚠️⚠️⚠️ NO SUBMENU PERMISSIONS TO SAVE!`);
        console.warn(`[syncPrivilegesFromBackend] ⚠️⚠️⚠️ This means backend is not returning submenu-level privileges!`);
      }

      console.log(`\n[syncPrivilegesFromBackend] ========== FINAL SUMMARY ==========`);
      console.log(`[syncPrivilegesFromBackend] Final visibleMenus (${visibleMenus.length}):`, visibleMenus);
      console.log(`[syncPrivilegesFromBackend] Final accessibleRoutes (${accessibleRoutes.length}):`, accessibleRoutes);
      console.log(`[syncPrivilegesFromBackend] Final submenuPermissions (${submenuPermissions.length}):`, submenuPermissions);

      // Debug: Check for unexpected routes
      const unexpectedReports = accessibleRoutes.filter(r => r.includes('/reports') && !r.includes('/reports/sales') && !r.includes('/reports/billing') && !r.includes('/reports/products'));
      if (unexpectedReports.length > 0) {
        console.warn(`[syncPrivilegesFromBackend] ⚠️⚠️⚠️ WARNING: Found unexpected REPORTS routes:`, unexpectedReports);
        console.warn(`[syncPrivilegesFromBackend] ⚠️⚠️⚠️ These should NOT be accessible for BILLING_PERSON!`);
      }

      const unexpectedSales = accessibleRoutes.filter(r => r.includes('/sales/returns'));
      if (unexpectedSales.length > 0) {
        console.warn(`[syncPrivilegesFromBackend] ⚠️⚠️⚠️ WARNING: Found unexpected SALES RETURN routes:`, unexpectedSales);
        console.warn(`[syncPrivilegesFromBackend] ⚠️⚠️⚠️ These should NOT be accessible if only specific submenu privileges exist!`);
      }

      console.log(`========== [syncPrivilegesFromBackend] DEBUG END ==========\n`);
    } else {
      console.warn(`[syncPrivilegesFromBackend] No privileges data in response`);
    }
  } catch (error) {
    console.error('[syncPrivilegesFromBackend] Error syncing privileges:', error);
    console.error('[syncPrivilegesFromBackend] Error details:', error.response?.data || error.message);
    // If backend sync fails, keep existing localStorage privileges
  }
};

/**
 * Save submenu-specific permissions to localStorage
 * This will allow granular control at the submenu level even though 
 * the backend only supports module-level permissions
 * 
 * @param {string} role - Role to save permissions for
 * @param {Array} submenuPermissions - Array of submenu permission objects
 */
export const saveSubmenuPermissions = (role, submenuPermissions) => {
  try {
    const key = `submenu_permissions_${role}`;
    localStorage.setItem(key, JSON.stringify(submenuPermissions));
    return true;
  } catch (error) {
    console.error('Error saving submenu permissions to localStorage:', error);
    return false;
  }
};

/**
 * Get submenu-specific permissions from localStorage
 * 
 * @param {string} role - Role to get permissions for
 * @returns {Array} Array of submenu permission objects
 */
export const getSubmenuPermissions = (role) => {
  try {
    const key = `submenu_permissions_${role}`;
    const permissions = localStorage.getItem(key);
    console.log(`[getSubmenuPermissions] Role: ${role}, Key: ${key}, Found: ${permissions ? 'YES' : 'NO'}`);
    if (permissions) {
      const parsed = JSON.parse(permissions);
      console.log(`[getSubmenuPermissions] Parsed ${parsed.length} submenu permissions:`, parsed);
      return parsed;
    } else {
      console.warn(`[getSubmenuPermissions] ⚠️ No submenu permissions found for role ${role}`);
      console.warn(`[getSubmenuPermissions] ⚠️ This may mean syncPrivilegesFromBackend hasn't run yet`);
    }
    return [];
  } catch (error) {
    console.error('[getSubmenuPermissions] Error getting submenu permissions from localStorage:', error);
    return [];
  }
};

export const clearAndReinitializePrivileges = () => {
  // Clear all role-specific privileges from localStorage
  Object.values(ROLES).forEach(role => {
    localStorage.removeItem(`privileges_${role}`);
    // IMPORTANT: Also clear submenu permissions to ensure fresh start
    localStorage.removeItem(`submenu_permissions_${role}`);
  });

  // Reinitialize with default privileges
  initializePrivileges();

  console.log('Privileges and submenu permissions cleared and reinitialized');
};

const privilegeService = {
  ROLES,
  MENUS,
  getRolePrivileges,
  saveRolePrivileges,
  hasRouteAccess,
  hasApiAccess,
  getVisibleMenuItems,
  initializePrivileges,
  clearAndReinitializePrivileges,
  syncPrivilegesFromBackend,
  fixPrivilegeRoutes,
  saveSubmenuPermissions,
  getSubmenuPermissions
};

export default privilegeService; 
